<template>
<div class="course-sessions">
    <div class="header">
    <h3>Sessions for {{ course.name }}</h3>
    <button @click="showCreateModal = true">+ Add Session</button>
    </div>

    <!-- Sessions Table -->
    <div v-if="sessions.length > 0" class="sessions-table">
    <table>
      <thead>
        <tr>
            <th>Date</th>
						<th>Day</th>
            <th>Time</th>
            <th>School</th>
            <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="session in sessions" :key="session.session_id">
            <td>{{ formatDate(session.start_at) }}</td>
            <td>{{ formatDay(session.start_at) }}</td>
						<td>{{ formatTime(session.start_at) }} - {{ formatTime(session.end_at) }}</td>
            <td>{{ session.school.name }}</td>
            <td>
							<button @click="viewAttendance(session.session_id)">Attendance</button>
							<button @click="openEditModal(session)">Edit</button>
      	      <button @click="confirmDelete(session.session_id)">Delete</button>
            </td>
        </tr>
      </tbody>
    </table>
    </div>
    <div v-else class="no-sessions">
    No sessions scheduled yet
    </div>

    <!-- Create/Edit Modal -->
    <SessionModal v-if="showCreateModal || currentSession" :course="course" :session="currentSession"
    @save="handleSave" @close="closeModal" />
</div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useQuery, useMutation } from '@vue/apollo-composable';
import SessionModal from './SessionModal.vue';
import { GET_COURSE_SESSIONS, DELETE_COURSE_SESSION } from '../graphql/queries';

// Props
const props = defineProps({
  course: {
    type: Object,
    required: true,
  }
});

const showCreateModal = ref(false);
const currentSession = ref(null);
const router = useRouter();

// Computed
const sessions = computed(() => result.value?.courseSessions || []);

// Load sessions when course changes
const { result, refetch } = useQuery(GET_COURSE_SESSIONS, 
	() => ({ course_id: props.course.course_id }),
	{ 
		enabled: computed(() => !!props.course?.course_id),
		fetchPolicy: 'network-only' 
	}
);

// Delete session
const { mutate: deleteSession, onDone } = useMutation(DELETE_COURSE_SESSION);
const confirmDelete = (id) => {
	if (confirm('Are you sure you want to delete this session?')) {
		deleteSession({ session_id: id });
	}
};
onDone(() => refetch());

const viewAttendance = (session_id) => {
	router.push(`/session/${session_id}/attendance`);
}

// Modal handlers
const openEditModal = (session) => {
	currentSession.value = session;
}

const closeModal = () => {
	showCreateModal.value = false;
	currentSession.value = null;
}

const handleSave = () => {
	closeModal();
	refetch(); // Refresh session list
}

// Format helpers
const formatDate = (dateString) => new Date(dateString).toLocaleDateString();
const formatDay = (dateString) => {
	const days = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];
	const day = new Date(dateString).getDay();
	return days[day];
};
const formatTime = (dateString) => new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
</script>
  
<style scoped>
.course-sessions {
	margin-top: 2rem;
	border-top: 1px solid #eee;
	padding-top: 1rem;
}

.header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 1rem;
}

table {
	width: 100%;
	border-collapse: collapse;
	margin-top: 1rem;
}

th, td {
	padding: 0.75rem;
	border: 1px solid #ddd;
	text-align: left;
}

th {
	background-color: #f5f5f5;
}

button {
	margin-right: 0.5rem;
	padding: 0.375rem 0.75rem;
	background-color: #f0f0f0;
	border: 1px solid #ddd;
	border-radius: 4px;
	cursor: pointer;
}

button:hover {
	background-color: #e0e0e0;
}

.no-sessions {
	padding: 1rem;
	background-color: #f9f9f9;
	border: 1px dashed #ddd;
	text-align: center;
	color: #666;
}
</style>